<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import CaptionService from '@/services/CaptionService'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCopy from '@/components/icon/IconCopy.vue'
import DictionaryEditor from './DictionaryEditor.vue'
import ReplacementsEditor from './ReplacementsEditor.vue'
import RecognitionOptions from './RecognitionOptions.vue'
import DictionaryImportModal from './DictionaryImportModal.vue'
import CopySettingsModal from './CopySettingsModal.vue'
import {
	REPLACEMENTS_MAX,
	draftFromSettings,
	settingsFromDraft,
	draftSnapshot,
	validateDraft,
	locateServerError,
	planImport,
	applyImport,
	newReplacementRow,
	exportText,
	exportCsv,
	termKey,
} from './captionRecognition'
import { FALLBACK_CATALOG, DELAY_RECOMMENDED, joinLanguageNames } from './captionLanguages'
import { buildTranslations, draftFromTranslations, genderAwareLangs, translatedLangs } from './captionTranslations'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
	flow: { type: Object, required: true },
	// A caption window is running: saves may apply live and Apply now is offered.
	running: { type: Boolean, default: false },
	// Language catalog from core; decides which spoken languages can filter profanity.
	catalog: { type: Object, default: () => FALLBACK_CATALOG },
})

const emit = defineEmits(['saved', 'applied', 'translations-cleared'])

const router = useRouter()
const { notify } = useNotification()

const saved = ref({})
const draft = ref(draftFromSettings({}))
const filter = ref('')
const saving = ref(false)
const applying = ref(false)
// { message, details: [] } from the last failed save.
const saveError = ref(null)
// Server field errors, kept with the value they were raised on so editing the
// field clears them: rows { [key]: { [field]: { message, value } } }, sections { [field]: {...} }.
const serverErrors = ref({ rows: {}, sections: {} })
// After a save while running: 'within_seconds' | 'next_start'; offers Apply now.
const appliesNotice = ref('')
// Set when a save turned gender-aware translation off along with Speaker labels.
const genderNotice = ref('')
const importOpen = ref(false)
const importFile = ref(null)
const copyOpen = ref(false)

const dictionary = ref(null)
const replacements = ref(null)
const options = ref(null)

function seed(settings) {
	saved.value = settings && typeof settings === 'object' ? settings : {}
	draft.value = draftFromSettings(saved.value)
	serverErrors.value = { rows: {}, sections: {} }
	saveError.value = null
	filter.value = ''
}

seed(props.flow.settings)
watch(() => props.flow.id, () => {
	seed(props.flow.settings)
	appliesNotice.value = ''
})
watch(() => props.running, (isRunning) => {
	if (!isRunning) appliesNotice.value = ''
})

// Profanity only applies to some spoken languages: for the others it is
// sent as 'off', and a saved mask/remove does not count as an edit.
const sendOpts = computed(() => ({ source: props.flow.source || '', catalog: props.catalog }))
const savedSnapshot = computed(() => draftSnapshot(draftFromSettings(saved.value), sendOpts.value))
const dirty = computed(() => draftSnapshot(draft.value, sendOpts.value) !== savedSnapshot.value)
const validation = computed(() => validateDraft(draft.value))

// Gender-aware translation needs Speaker labels: core refuses to turn
// `speakers` off while any translation has `genderAware`, so a save that does
// first saves the translations without it (same PUT as the languages panel).
const savedGenderAware = computed(() => {
	const translated = translatedLangs(props.flow.source || '', props.flow.langs || [])
	return genderAwareLangs(props.flow.translations).filter((l) => translated.some((t) => t.toLowerCase() === l.toLowerCase()))
})
const clearsGenderAware = computed(() => !draft.value.speakers && savedGenderAware.value.length > 0)
const speakersNote = computed(() => {
	if (!clearsGenderAware.value) return ''
	const names = joinLanguageNames(savedGenderAware.value, props.catalog)
	return `Saving also turns off gender-aware translation for ${names}, which needs Speaker labels.${props.running ? ' Captions restart briefly.' : ''}`
})

async function clearGenderAware() {
	const ctx = { source: props.flow.source || '', langs: props.flow.langs || [], catalog: props.catalog }
	const translations = buildTranslations(draftFromTranslations(props.flow.translations, ctx), { ...ctx, speakers: false })
	const res = await CaptionService.updateFlow(props.flow.id, { source: ctx.source, langs: ctx.langs, delaySec: DELAY_RECOMMENDED, translations })
	emit('translations-cleared', res)
}

// ------------------------------------------------------------------ Errors
const fieldValue = (row, field) => (field === 'soundsLike' ? JSON.stringify(row.soundsLike) : row[field])
const sectionValue = (field) => JSON.stringify(field === 'vocab' || field === 'replacements' ? settingsFromDraft(draft.value).settings[field] : draft.value[field])

const rowErrors = computed(() => {
	const out = {}
	for (const [key, fields] of Object.entries(validation.value.rows)) out[key] = { ...fields }
	const byKey = new Map([...draft.value.vocab, ...draft.value.replacements].map((r) => [r.key, r]))
	for (const [key, fields] of Object.entries(serverErrors.value.rows)) {
		const row = byKey.get(key)
		if (!row) continue
		for (const [field, err] of Object.entries(fields)) {
			if (fieldValue(row, field) !== err.value) continue
			out[key] = out[key] || {}
			if (!out[key][field]) out[key][field] = err.message
		}
	}
	return out
})
const sectionErrors = computed(() => {
	const out = { ...validation.value.sections }
	for (const [field, err] of Object.entries(serverErrors.value.sections)) {
		if (!out[field] && sectionValue(field) === err.value) out[field] = err.message
	}
	return out
})
const errorCount = computed(() =>
	Object.values(rowErrors.value).reduce((n, fields) => n + Object.keys(fields).length, 0)
	+ Object.keys(sectionErrors.value).length)
const dupeCount = computed(() => Object.keys(validation.value.dupes).length)
const canSave = computed(() => dirty.value && errorCount.value === 0 && !saving.value)

const vocabRowErrors = computed(() => pick(rowErrors.value, draft.value.vocab))
const replacementRowErrors = computed(() => pick(rowErrors.value, draft.value.replacements))
function pick(all, rows) {
	const out = {}
	for (const r of rows) if (all[r.key]) out[r.key] = all[r.key]
	return out
}

function jumpToFirstError() {
	const vocabRow = draft.value.vocab.find((r) => rowErrors.value[r.key])
	if (vocabRow) {
		filter.value = ''
		dictionary.value?.focusRow(vocabRow.key, rowErrors.value[vocabRow.key].term ? 'term' : 'soundsLike')
		return
	}
	const replRow = draft.value.replacements.find((r) => rowErrors.value[r.key])
	if (replRow) {
		replacements.value?.focusRow(replRow.key, rowErrors.value[replRow.key].from ? 'from' : 'to')
		return
	}
	const field = Object.keys(sectionErrors.value)[0]
	if (field === 'vocab') document.getElementById('dictionary-heading')?.scrollIntoView({ block: 'center', behavior: 'smooth' })
	else if (field === 'replacements') document.getElementById('replacements-heading')?.scrollIntoView({ block: 'center', behavior: 'smooth' })
	else if (field) options.value?.focusField(field)
}

// -------------------------------------------------------------------- Save
async function save() {
	if (!canSave.value) {
		if (errorCount.value) jumpToFirstError()
		return
	}
	saving.value = true
	saveError.value = null
	genderNotice.value = ''
	const { settings, vocabKeys, replacementKeys } = settingsFromDraft(draft.value, sendOpts.value)
	if (!settings.speakers && clearsGenderAware.value) {
		try {
			await clearGenderAware()
			genderNotice.value = 'Gender-aware translation was turned off because Speaker labels is off.'
		} catch (err) {
			saveError.value = { message: err?.message || "Couldn't turn off gender-aware translation, so Speaker labels stay on. Try again.", details: [] }
			saving.value = false
			return
		}
	}
	try {
		const res = await CaptionService.updateRecognition(props.flow.id, settings)
		const next = res.flow?.settings && Object.keys(res.flow.settings).length ? res.flow.settings : settings
		seed(next)
		emit('saved', res)
		track(EVENTS.CAPTIONING_DICTIONARY_SAVED, {
			flowId: props.flow.id,
			vocabCount: (next.vocab || []).length,
			replacementsCount: (next.replacements || []).length,
			removeDisfluencies: !!next.removeDisfluencies,
			profanity: next.profanity || 'off',
			speakers: !!next.speakers,
			outputLocale: next.outputLocale || null,
		})
		const deduped = res.dedupedCount
			? ` ${res.dedupedCount} duplicate ${res.dedupedCount === 1 ? 'term was' : 'terms were'} removed.`
			: ''
		notify({
			group: 'success',
			text: (res.appliesAt === 'within_seconds'
				? 'Saved. Captions pick this up within about 10 seconds.'
				: 'Saved. Applies the next time captions start.') + deduped,
		})
		appliesNotice.value = props.running ? res.appliesAt : ''
	} catch (err) {
		if (err?.code === 'captions_invalid_settings' && Array.isArray(err?.errors)) {
			applyServerErrors(err.errors, vocabKeys, replacementKeys)
		} else {
			saveError.value = { message: err?.message || 'Could not save the recognition settings.', details: [] }
		}
	} finally {
		saving.value = false
	}
}

function applyServerErrors(errors, vocabKeys, replacementKeys) {
	const rows = {}
	const sections = {}
	const general = []
	const byKey = new Map([...draft.value.vocab, ...draft.value.replacements].map((r) => [r.key, r]))
	for (const e of errors) {
		const message = String(e?.message || 'Invalid value.')
		const where = locateServerError(e?.path, vocabKeys, replacementKeys)
		if (where.kind === 'row' && byKey.has(where.key)) {
			rows[where.key] = rows[where.key] || {}
			rows[where.key][where.field] = { message, value: fieldValue(byKey.get(where.key), where.field) }
		} else if (where.kind === 'section') {
			sections[where.field] = { message, value: sectionValue(where.field) }
		} else {
			general.push(message)
		}
	}
	serverErrors.value = { rows, sections }
	const n = errors.length
	saveError.value = {
		message: `${n === 1 ? 'One setting needs' : `${n} settings need`} fixing before this can be saved.`,
		details: general,
	}
	jumpToFirstError()
}

// ------------------------------------------------------------------ Discard
// Large edits (an import, a copy, a long paste) ask before being thrown away.
const bigChange = computed(() => {
	const before = new Set((saved.value.vocab || []).map((v) => termKey(typeof v === 'string' ? v : v?.content)))
	const after = new Set(draft.value.vocab.map((r) => termKey(r.term)).filter(Boolean))
	let diff = 0
	for (const k of after) if (!before.has(k)) diff += 1
	for (const k of before) if (!after.has(k)) diff += 1
	return diff > 10
})

function discard() {
	if (bigChange.value) showModal('modal-recognition-discard')
	else resetDraft()
}

function resetDraft() {
	seed(saved.value)
}

// ---------------------------------------------------------------- Apply now
async function applyNow() {
	if (applying.value) return
	applying.value = true
	try {
		const res = await CaptionService.applyRecognition(props.flow.id)
		appliesNotice.value = ''
		emit('applied', res)
		track(EVENTS.CAPTIONING_SETTINGS_APPLIED_NOW, { flowId: props.flow.id })
		notify({ group: 'success', text: 'Restarting captions with your settings. They\'re back in about 10 seconds.' })
	} catch (err) {
		if (err?.code === 'window_not_running' || err?.status === 409) {
			appliesNotice.value = ''
			notify({ group: 'info', title: 'Captions aren\'t running', text: 'Your saved settings apply the next time captions start.' })
		} else {
			notify({ group: 'error', title: 'Could not restart captions', text: err?.message })
		}
	} finally {
		applying.value = false
	}
}

// ---------------------------------------------------------- Import / export
function onImportFile(file) {
	importFile.value = file
	importOpen.value = true
}

function onImportConfirm({ mode, plan }) {
	draft.value.vocab = applyImport(draft.value.vocab, plan, mode)
	filter.value = ''
	track(EVENTS.CAPTIONING_DICTIONARY_IMPORTED, { added: plan.toAdd.length, duplicates: plan.duplicates, errors: plan.errors, mode })
	notify({ group: 'success', text: `Added ${plan.toAdd.length} ${plan.toAdd.length === 1 ? 'term' : 'terms'} from ${importFile.value?.name || 'the file'}. Review, then save.` })
}

function exportDictionary(format) {
	const content = format === 'csv' ? exportCsv(draft.value.vocab) : exportText(draft.value.vocab)
	const slug = String(props.flow.streamName || 'captions').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'captions'
	const blob = new Blob([content], { type: format === 'csv' ? 'text/csv;charset=utf-8' : 'text/plain;charset=utf-8' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = `${slug}-dictionary.${format}`
	document.body.appendChild(a)
	a.click()
	a.remove()
	setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// --------------------------------------------------------------------- Copy
const currentVocabCount = computed(() => validation.value.vocabCount)
const currentReplacementsCount = computed(() => settingsFromDraft(draft.value).settings.replacements.length)

function onCopy({ source, settings, mode }) {
	const incoming = draftFromSettings(settings)
	if (mode === 'replace') {
		draft.value = incoming
	} else {
		const entries = incoming.vocab.filter((r) => r.term.trim()).map((r, i) => ({ line: i + 1, term: r.term.trim(), soundsLike: r.soundsLike }))
		const plan = planImport({ entries, errors: [] }, draft.value.vocab, 'merge')
		draft.value.vocab = applyImport(draft.value.vocab, plan, 'merge')
		const have = new Set(draft.value.replacements.map((r) => `${r.from}\u0000${r.to}`))
		const kept = draft.value.replacements.filter((r) => r.from || r.to)
		for (const r of incoming.replacements) {
			if (kept.length >= REPLACEMENTS_MAX) break
			const id = `${r.from}\u0000${r.to}`
			if (have.has(id)) continue
			have.add(id)
			kept.push(newReplacementRow(r.from, r.to))
		}
		draft.value.replacements = kept
	}
	filter.value = ''
	track(EVENTS.CAPTIONING_DICTIONARY_COPIED, { flowId: props.flow.id, sourceFlowId: source.id, mode, vocabCount: validation.value.vocabCount })
	notify({ group: 'success', text: `Copied from ${source.streamName || 'the other stream'}. Review the changes, then save.` })
}

// ---------------------------------------------------------- Leave guard
let leaveTarget = null
let leaving = false
onBeforeRouteLeave((to) => {
	if (!dirty.value || leaving) return true
	leaveTarget = to
	showModal('modal-recognition-leave')
	return false
})
function confirmLeave() {
	if (!leaveTarget) return
	leaving = true
	router.push(leaveTarget.fullPath).catch(() => { leaving = false })
}
function onBeforeUnload(e) {
	if (!dirty.value) return
	e.preventDefault()
	e.returnValue = ''
}
onMounted(() => window.addEventListener('beforeunload', onBeforeUnload))
onBeforeUnmount(() => window.removeEventListener('beforeunload', onBeforeUnload))
</script>

<template>
	<div class="contents">
		<section class="flex flex-col gap-6 rounded-xl bg-surface-2 p-4 sm:p-5" aria-labelledby="recognition-heading" data-testid="recognition">
			<header class="flex flex-wrap items-start justify-between gap-3">
				<div class="flex min-w-0 flex-col gap-1">
					<h2 id="recognition-heading" class="m-0 text-base font-semibold text-surface-text-2">Recognition</h2>
					<p class="m-0 text-tiny text-surface-8">
						Teach captions the names and words your stream uses, and choose how speech is written out.
						<template v-if="running"> You can edit while captions run.</template>
					</p>
				</div>
				<FormButton type="secondary" size="sm" :disabled="saving" data-testid="copy-from-stream" @click="copyOpen = true">
					<icon-base width="14" height="14" class="mr-1.5"><icon-copy /></icon-base>
					Copy from another stream
				</FormButton>
			</header>

			<Alert v-if="appliesNotice && running" type="normal" role="status" data-testid="applies-notice">
				<span class="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
					<span class="flex-1">
						<template v-if="appliesNotice === 'within_seconds'">Running captions switch to these settings within about 10 seconds. Restart now to apply them immediately.</template>
						<template v-else>These settings take effect the next time captions start. Restart now to apply them immediately.</template>
					</span>
					<FormButton type="secondary" size="sm" :is-loading="applying" :disabled="applying" data-testid="apply-now" @click="showModal('modal-recognition-apply')">Apply now</FormButton>
				</span>
			</Alert>

			<Alert v-if="genderNotice" type="normal" role="status" data-testid="gender-aware-cleared">{{ genderNotice }}</Alert>

			<Alert v-if="saveError" type="error" role="alert" data-testid="recognition-error">
				<span class="flex flex-col gap-1">
					<span>{{ saveError.message }}</span>
					<span v-for="(d, i) in saveError.details" :key="i" class="text-xs">{{ d }}</span>
				</span>
			</Alert>

			<DictionaryEditor
				ref="dictionary"
				v-model:rows="draft.vocab"
				v-model:filter="filter"
				:errors="vocabRowErrors"
				:dupes="validation.dupes"
				:vocab-count="validation.vocabCount"
				:vocab-chars="validation.vocabChars"
				:section-error="sectionErrors.vocab || ''"
				:disabled="saving"
				@import-file="onImportFile"
				@export="exportDictionary"
			/>

			<hr class="m-0 border-surface-3">

			<ReplacementsEditor
				ref="replacements"
				v-model:rows="draft.replacements"
				:errors="replacementRowErrors"
				:section-error="sectionErrors.replacements || ''"
				:disabled="saving"
			/>

			<hr class="m-0 border-surface-3">

			<RecognitionOptions
				ref="options"
				v-model:remove-disfluencies="draft.removeDisfluencies"
				v-model:profanity="draft.profanity"
				v-model:speakers="draft.speakers"
				v-model:max-speakers="draft.maxSpeakers"
				v-model:output-locale="draft.outputLocale"
				v-model:max-delay-sec="draft.maxDelaySec"
				:source="flow.source || ''"
				:catalog="catalog"
				:speakers-note="speakersNote"
				:errors="sectionErrors"
				:disabled="saving"
			/>
		</section>

		<!-- Sticky to the page (the wrapper is display: contents), shown only with unsaved recognition edits. -->
		<div v-if="dirty" class="sticky bottom-3 z-30" role="region" aria-label="Unsaved recognition changes" data-testid="recognition-savebar">
			<div class="flex flex-col gap-3 rounded-xl bg-surface-3 px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.45)] ring-1 ring-white/10 sm:flex-row sm:items-center">
				<p class="m-0 flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 text-tiny text-surface-text-1" aria-live="polite">
					<span class="font-semibold text-surface-text-2">Recognition</span>
					<span class="text-surface-7">·</span>
					<span>Unsaved changes</span>
					<template v-if="errorCount">
						<span class="text-surface-7">·</span>
						<button type="button" class="font-medium text-error-light underline-offset-2 hover:underline" data-testid="savebar-errors" @click="jumpToFirstError">
							{{ errorCount }} {{ errorCount === 1 ? 'error' : 'errors' }} to fix
						</button>
					</template>
					<template v-else-if="dupeCount">
						<span class="text-surface-7">·</span>
						<span class="text-gold" data-testid="savebar-dupes">{{ dupeCount }} {{ dupeCount === 1 ? 'duplicate' : 'duplicates' }} will be dropped</span>
					</template>
				</p>
				<div class="grid grid-cols-2 gap-2 sm:flex">
					<FormButton type="secondary" size="md" class="justify-center" :disabled="saving" data-testid="discard" @click="discard">Discard</FormButton>
					<FormButton type="main" size="md" class="justify-center" :disabled="!canSave" :is-loading="saving" data-testid="save-recognition" @click="save">Save recognition</FormButton>
				</div>
			</div>
		</div>

		<DictionaryImportModal v-model="importOpen" :file="importFile" :existing-rows="draft.vocab" @confirm="onImportConfirm" />
		<CopySettingsModal
			v-model="copyOpen"
			:flow-id="flow.id"
			:current-vocab-count="currentVocabCount"
			:current-replacements-count="currentReplacementsCount"
			@copy="onCopy"
		/>
		<ConfirmModal
			modal-id="modal-recognition-apply"
			title="Restart captions now to apply?"
			message="Viewers lose captions for about 10 seconds."
			ok-text="Restart now"
			cancel-text="Not now"
			modal-type="danger"
			@modal-confirm="applyNow"
		/>
		<ConfirmModal
			modal-id="modal-recognition-discard"
			title="Discard unsaved changes?"
			message="Your recognition edits since the last save will be lost."
			ok-text="Discard"
			cancel-text="Keep editing"
			modal-type="danger"
			@modal-confirm="resetDraft"
		/>
		<ConfirmModal
			modal-id="modal-recognition-leave"
			title="Leave without saving?"
			message="Your recognition changes will be lost."
			ok-text="Leave"
			cancel-text="Stay"
			modal-type="danger"
			@modal-confirm="confirmLeave"
		/>
	</div>
</template>
